import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';

const SalesBarChart = ({ salesData, year }) => {
    const targetSales = salesData?.targetSales || 0;
    const actualSalesTotal = 
        (salesData?.actual?.project || 0) + 
        (salesData?.actual?.maintenance || 0);
    const forecastSalesTotal = 
        (salesData?.forecast?.project || 0) + 
        (salesData?.forecast?.maintenance || 0);

    const targetProfit = salesData?.targetProfit || 0;
    const actualProfitTotal = 
        (salesData?.actualProfit?.project || 0) + 
        (salesData?.actualProfit?.maintenance || 0);
    const forecastProfitTotal = 
        (salesData?.forecastProfit?.project || 0) + 
        (salesData?.forecastProfit?.maintenance || 0);

    const salesActualRate = targetSales > 0 ? Number(((actualSalesTotal / targetSales) * 100).toFixed(1)) : 0;
    const salesForecastRate = targetSales > 0 ? Number(((forecastSalesTotal / targetSales) * 100).toFixed(1)) : 0;
    const salesRemainingRate = Math.max(0, Number((100 - (salesActualRate + salesForecastRate)).toFixed(1)));

    const profitActualRate = targetProfit > 0 ? Number(((actualProfitTotal / targetProfit) * 100).toFixed(1)) : 0;
    const profitForecastRate = targetProfit > 0 ? Number(((forecastProfitTotal / targetProfit) * 100).toFixed(1)) : 0;
    const profitRemainingRate = Math.max(0, Number((100 - (profitActualRate + profitForecastRate)).toFixed(1)));

    const salesDiff = actualSalesTotal - targetSales; 
    const profitDiff = actualProfitTotal - targetProfit; 

    const rowsData = [
        {
            label: '매출액',
            actual: actualSalesTotal,
            actualRate: salesActualRate,
            forecast: forecastSalesTotal,
            forecastRate: salesForecastRate,
            diff: salesDiff,
            chartData: [{ name: '매출액', 수주율: salesActualRate, 포캐스팅율: salesForecastRate, 남은목표율: salesRemainingRate }]
        },
        {
            label: '매출 이익',
            actual: actualProfitTotal,
            actualRate: profitActualRate,
            forecast: forecastProfitTotal,
            forecastRate: profitForecastRate,
            diff: profitDiff,
            chartData: [{ name: '매출 이익', 수주율: profitActualRate, 포캐스팅율: profitForecastRate, 남은목표율: profitRemainingRate }]
        }
    ];

    const formatXAxis = (value) => `${value}%`;
    return (
        <div style={{ 
            width: '100%', 
            height: '100%',
            display: 'flex', 
            flexDirection: 'column', 
            background: '#ffffff',
            border: '1px solid #E2E8F0', 
            borderRadius: '12px',
            overflow: 'hidden',
            boxSizing: 'border-box'
        }}>
            
            <div style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 16px', 
                backgroundColor: '#F1F5F9', 
                borderBottom: '1px solid #E2E8F0', 
                boxSizing: 'border-box'
            }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#334155', letterSpacing: '-0.3px' }}>
                    {year}년 수주 및 포캐스팅
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    {['수주', '포캐스팅', '남은목표'].map((label, idx) => {
                        const colors = ['#2EB886', '#4EA3E6', '#E2E8F0'];
                        return (
                            <div key={label} style={{ display: 'flex', alignItems: 'center', fontSize: '11px', fontWeight: '700' }}>
                                <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '2px', backgroundColor: colors[idx], marginRight: '4px' }}></span>
                                <span style={{ color: '#475569' }}>{label}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div style={{ flex: '1', width: '100%', padding: '20px 20px 12px 20px', display: 'flex', flexDirection: 'column', gap: '28px', boxSizing: 'border-box', position: 'relative' }}>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', width: '100%', zIndex: 2, position: 'relative' }}>
                    {rowsData.map((row) => (
                        <div key={row.label} style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '20px' }}>
                                <div style={{ width: '65px', fontSize: '13px', fontWeight: '700', color: '#0F172A', flexShrink: 0 }}>{row.label}</div>
                                <div style={{ display: 'flex', gap: '14px', fontSize: '13px', fontWeight: '600', color: '#475569', flex: 1, justifyContent: 'flex-end' }}>
                                    <div style={{ width: '185px', display: 'flex', alignItems: 'center' }}>
                                        <span style={{ width: '38px', flexShrink: 0 }}>수주:</span>
                                        <span style={{ width: '85px', textAlign: 'right', color: '#2EB886', fontWeight: '700' }}>{row.actual.toLocaleString()}</span>
                                        <span style={{ width: '14px', paddingLeft: '2px', flexShrink: 0 }}>원</span>
                                        <span style={{ color: '#2EB886', fontWeight: '700', fontSize: '12px', width: '44px', textAlign: 'right' }}>({row.actualRate}%)</span>
                                    </div>
                                    <div style={{ width: '208px', display: 'flex', alignItems: 'center' }}>
                                        <span style={{ width: '58px', flexShrink: 0 }}>포캐스팅:</span>
                                        <span style={{ width: '85px', textAlign: 'right', color: '#4EA3E6', fontWeight: '700' }}>{row.forecast.toLocaleString()}</span>
                                        <span style={{ width: '14px', paddingLeft: '2px', flexShrink: 0 }}>원</span>
                                        <span style={{ color: '#4EA3E6', fontWeight: '700', fontSize: '12px', width: '#48px', textAlign: 'right' }}>({row.forecastRate}%)</span>
                                    </div>
                                    <div style={{ paddingLeft: '14px', color: '#64748B', display: 'flex', alignItems: 'center' }}>
                                        <span style={{ width: '58px', flexShrink: 0 }}>목표현황:</span>
                                        {row.diff >= 0 ? (
                                            <>
                                                <span style={{ width: '85px', textAlign: 'right', color: '#0F172A', fontWeight: '700' }}>{row.actual.toLocaleString()}</span>
                                                <span style={{ width: '14px', paddingLeft: '2px', flexShrink: 0, color: '#0F172A', fontWeight: '700' }}>원</span>
                                                <span style={{ color: '#2563EB', fontWeight: '700', fontSize: '12px', width: '#42px', paddingLeft: '4px' }}>(초과)</span>
                                            </>
                                        ) : (
                                            <>
                                                <span style={{ width: '85px', textAlign: 'right', color: '#0F172A', fontWeight: '700' }}>{Math.abs(row.diff).toLocaleString()}</span>
                                                <span style={{ width: '14px', paddingLeft: '2px', flexShrink: 0, color: '#0F172A', fontWeight: '700' }}>원</span>
                                                <span style={{ color: '#EF4444', fontWeight: '700', fontSize: '12px', width: '#42px', paddingLeft: '4px' }}>(부족)</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div style={{ width: '100%', height: '32px' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart 
                                        data={row.chartData} 
                                        layout="vertical" 
                                        margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                                    >
                                        {/* 1. 배경 그리드를 차트 내부에 첫 번째로 배치하여 바 밑에 깔리도록 함 */}
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F1F5F9" />
                                        
                                        {/* 2. 축 설정 (수치 바인딩 및 숨김 처리) */}
                                        <XAxis type="number" domain={[0, 100]} hide />
                                        <YAxis type="category" dataKey="name" hide />
                                        
                                        {/* 3. 누적(Stack) 처리된 데이터 바들 */}
                                        <Bar dataKey="수주율" fill="#2EB886" stackId="ratio" maxBarSize={24} />
                                        <Bar dataKey="포캐스팅율" fill="#4EA3E6" stackId="ratio" maxBarSize={24} />
                                        <Bar dataKey="남은목표율" fill="#E2E8F0" stackId="ratio" maxBarSize={24} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    ))}
                </div>

                <div style={{ width: '100%', height: '36px', marginTop: '-20px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={[{}]} layout="vertical" margin={{ top: 0, right: 0, left: -95, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F1F5F9" />
                            <XAxis type="number" domain={[0, 100]} tickFormatter={formatXAxis} tick={{ fontSize: 11, fill: '#64748B', fontWeight: '600', dy: 8 }} tickLine={false} axisLine={{ stroke: '#E2E8F0' }} />
                            <YAxis type="category" hide />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

            </div>
        </div>
    );
};

export default SalesBarChart;
